/**
 * SlavePreflightCard — Pre-flight confirmation card shown before delegating
 * to the slave Claude Code agent for site/API testing.
 *
 * Displays target domain, scope, prior findings count, authorization status,
 * discovered API endpoints (with selection), and auth configuration.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Checkbox,
  Collapse,
  IconButton,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Close as CancelIcon,
  Security as SecurityIcon,
  Language as DomainIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Api as ApiIcon,
  VpnKey as AuthIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
} from '@mui/icons-material';
import {
  SlavePreflightData,
  SlavePreflightEndpoint,
  SlavePreflightConfirmOptions,
  AuthConfig,
  AuthConfigType,
} from '../types';
import { authorizeDomain } from '../services/project.service';

// ── Method badge ──

const METHOD_COLORS: Record<string, string> = {
  GET: '#2e7d32', POST: '#1565c0', PUT: '#e65100',
  PATCH: '#6a1b9a', DELETE: '#c62828', HEAD: '#37474f', OPTIONS: '#757575',
};

const MethodBadge: React.FC<{ method: string }> = ({ method }) => (
  <Typography component="span" sx={{
    fontSize: '0.6rem', fontWeight: 700, color: '#fff', px: 0.5, py: 0.1,
    borderRadius: 0.5, bgcolor: METHOD_COLORS[method?.toUpperCase()] || '#757575',
    fontFamily: 'monospace', lineHeight: 1.4, display: 'inline-block',
    minWidth: 32, textAlign: 'center',
  }}>
    {method?.toUpperCase()}
  </Typography>
);

// ── Props ──

interface SlavePreflightCardProps {
  data: SlavePreflightData;
  /** Resolves once the run was approved and re-sent; rejects with the reason. */
  onConfirm: (
    originalRequest: string,
    options?: SlavePreflightConfirmOptions
  ) => Promise<void> | void;
  onCancel?: () => void;
  /** TMS project id — required to call the (org-scoped) authorize endpoint. */
  projectId?: string;
  /** Whether the current user is an org admin (may authorize new domains). */
  canAuthorize?: boolean;
}

const SlavePreflightCard: React.FC<SlavePreflightCardProps> = ({
  data,
  onConfirm,
  onCancel,
  projectId,
  canAuthorize = false,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Domain authorization state. The domain must be authorized before the run
  // can start; an org admin can authorize it inline here after attesting.
  const [localAuthorized, setLocalAuthorized] = useState(false);
  const [attested, setAttested] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [authorizeError, setAuthorizeError] = useState<string | null>(null);
  // No target domain (e.g. "Run test case N", an API test with no URL): there is
  // nothing to authorize, so the whole authorization section is hidden and the
  // card never posts an empty domain.
  const hasDomain = Boolean((data.domain || '').trim());
  const isAuthorized = !hasDomain || data.authorized_domain || localAuthorized;

  // Endpoint selection state
  const endpoints = data.discovered_endpoints || [];
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set(endpoints.map((_, i) => i))
  );
  const [endpointsExpanded, setEndpointsExpanded] = useState(endpoints.length <= 20);

  // Auth config state
  const [authExpanded, setAuthExpanded] = useState(false);
  const [authType, setAuthType] = useState<AuthConfigType>('none');
  const [bearerToken, setBearerToken] = useState('');
  const [apiKeyHeader, setApiKeyHeader] = useState('X-API-Key');
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [loginMethod, setLoginMethod] = useState('POST');
  const [loginBody, setLoginBody] = useState('');
  const [tokenPath, setTokenPath] = useState('data.token');

  const isDisabled = submitted || cancelled;

  // Group endpoints by tag
  const groupedEndpoints = useMemo(() => {
    const groups: Record<string, { ep: SlavePreflightEndpoint; index: number }[]> = {};
    endpoints.forEach((ep, i) => {
      const tag = ep.tag || 'Other';
      if (!groups[tag]) groups[tag] = [];
      groups[tag].push({ ep, index: i });
    });
    return groups;
  }, [endpoints]);

  const allSelected = selectedIndices.size === endpoints.length;
  const noneSelected = selectedIndices.size === 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(endpoints.map((_, i) => i)));
    }
  };

  const toggleIndex = (index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const buildAuthConfig = (): AuthConfig | undefined => {
    if (authType === 'none') return undefined;
    if (authType === 'bearer') {
      return { type: 'bearer', bearer_token: bearerToken };
    }
    if (authType === 'api_key') {
      return { type: 'api_key', api_key_header: apiKeyHeader, api_key_value: apiKeyValue };
    }
    if (authType === 'login_endpoint') {
      return {
        type: 'login_endpoint',
        login_url: loginUrl,
        login_method: loginMethod,
        login_body: loginBody,
        token_path: tokenPath,
      };
    }
    return undefined;
  };

  const handleConfirm = async () => {
    if (isDisabled) return;
    setSubmitted(true);
    setConfirmError(null);

    const selected = endpoints.length > 0
      ? endpoints.filter((_, i) => selectedIndices.has(i))
      : undefined;

    try {
      await onConfirm(data.original_request, {
        approval_id: data.approval_id,
        selected_endpoints: selected,
        auth_config: buildAuthConfig(),
        total_discovered_count: endpoints.length,
      });
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const expired = status === 410 || !data.approval_id;
      setConfirmError(
        expired
          ? 'This approval expired, ask again.'
          : e instanceof Error
            ? e.message
            : 'Failed to confirm'
      );
      // An expired approval cannot be retried from this card.
      if (!expired) setSubmitted(false);
    }
  };

  const handleCancel = () => {
    if (isDisabled) return;
    setCancelled(true);
    onCancel?.();
  };

  const handleAuthorize = async () => {
    if (!projectId || !attested || authorizing || !canAuthorize) return;
    setAuthorizing(true);
    setAuthorizeError(null);
    try {
      await authorizeDomain(projectId, data.domain);
      setLocalAuthorized(true);
    } catch (e) {
      setAuthorizeError(e instanceof Error ? e.message : 'Failed to authorize domain');
    } finally {
      setAuthorizing(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        p: 2,
        border: 2,
        borderColor: (theme) => alpha(theme.palette.info.main, 0.4),
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Pre-flight: Confirm Testing Scope
      </Typography>

      {data.refused_reason && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          The previous confirmation was refused ({data.refused_reason}). Confirm again to start.
        </Alert>
      )}

      {/* Target domain */}
      {hasDomain && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <DomainIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={500}>
          Target:
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {data.domain}
        </Typography>
        <Chip
          label={isAuthorized ? 'Authorized' : 'Not authorized'}
          size="small"
          color={isAuthorized ? 'success' : 'warning'}
          variant="outlined"
          icon={<SecurityIcon fontSize="small" />}
        />
      </Box>
      )}

      {/* ── Domain authorization gate ── */}
      {/* A domain must be authorized (an admin attestation that the org may run */}
      {/* automated tests against it) before the run can start. Shown only when  */}
      {/* the target isn't authorized yet.                                       */}
      {!isAuthorized && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.warning.main, 0.5),
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            This domain isn't authorized for testing yet
          </Typography>
          {canAuthorize ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Authorizing enables <b>{data.domain}</b> for your whole organization.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={attested}
                    disabled={authorizing || isDisabled}
                    onChange={(e) => setAttested(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption">
                    I confirm my organization owns or is permitted to run automated
                    tests against this domain.
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', mb: 0.5 }}
              />
              {authorizeError && (
                <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>
                  {authorizeError}
                </Alert>
              )}
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={authorizing ? <CircularProgress size={14} color="inherit" /> : <SecurityIcon />}
                onClick={handleAuthorize}
                disabled={!attested || authorizing || isDisabled || !projectId}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {authorizing ? 'Authorizing…' : 'Authorize domain'}
              </Button>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Only an organization admin can authorize a new domain. Ask an admin to
              authorize <b>{data.domain}</b>, then re-run this test.
            </Typography>
          )}
        </Box>
      )}

      {/* Scope summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Scope:
        </Typography>
        <Typography variant="body2">
          {data.scope_summary}
        </Typography>
      </Box>

      {/* Prior findings */}
      {data.prior_findings_count > 0 && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
          {data.prior_findings_count} prior finding{data.prior_findings_count !== 1 ? 's' : ''} from
          previous sessions will be used for regression tracking.
        </Alert>
      )}

      {/* ── Discovered Endpoints Section ── */}
      {endpoints.length > 0 && (
        <Box sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box
            onClick={() => setEndpointsExpanded(!endpointsExpanded)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 1, cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <ApiIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              Discovered Endpoints ({endpoints.length})
            </Typography>
            <Chip
              label={`${selectedIndices.size} selected`}
              size="small"
              color={noneSelected ? 'error' : 'primary'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
            {endpointsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>

          <Collapse in={endpointsExpanded}>
            <Box sx={{ px: 1.5, pb: 1 }}>
              {/* Select all / deselect all */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Button
                  size="small"
                  onClick={toggleAll}
                  disabled={isDisabled}
                  startIcon={allSelected ? <CheckBoxIcon fontSize="small" /> : <CheckBoxBlankIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              {/* Grouped endpoint list */}
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {Object.entries(groupedEndpoints).map(([tag, items]) => (
                  <Box key={tag} sx={{ mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary"
                      sx={{ fontSize: '0.65rem', display: 'block', mb: 0.25, textTransform: 'uppercase' }}>
                      {tag}
                    </Typography>
                    {items.map(({ ep, index }) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.5,
                          py: 0.25, px: 0.5,
                          '&:hover': { bgcolor: 'action.hover' },
                          borderRadius: 0.5,
                          opacity: selectedIndices.has(index) ? 1 : 0.5,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedIndices.has(index)}
                          onChange={() => toggleIndex(index)}
                          disabled={isDisabled}
                          sx={{ p: 0.25 }}
                        />
                        <MethodBadge method={ep.method} />
                        <Typography variant="caption" sx={{
                          fontFamily: 'monospace', fontSize: '0.7rem', flex: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {ep.path}
                        </Typography>
                        {ep.auth_required && (
                          <AuthIcon sx={{ fontSize: '0.7rem', color: 'warning.main' }} />
                        )}
                        {ep.description && (
                          <Typography variant="caption" color="text.secondary" sx={{
                            fontSize: '0.6rem', maxWidth: 200,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {ep.description}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}

      {endpoints.length === 0 && data.domain !== 'unknown' && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.25 }}>
          No API endpoints discovered during pre-flight. The agent will attempt discovery during execution.
        </Alert>
      )}

      {/* ── Authentication Section ── */}
      <Box sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box
          onClick={() => setAuthExpanded(!authExpanded)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 1, cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <AuthIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            Authentication
          </Typography>
          {authType !== 'none' && (
            <Chip
              label={authType === 'bearer' ? 'Bearer Token' : authType === 'api_key' ? 'API Key' : 'Login Endpoint'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          )}
          {data.auth_patterns && data.auth_patterns.length > 0 && (
            <Chip
              label={`Detected: ${data.auth_patterns.join(', ')}`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.6rem' }}
            />
          )}
          {authExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>

        <Collapse in={authExpanded}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <RadioGroup
              value={authType}
              onChange={(e) => setAuthType(e.target.value as AuthConfigType)}
            >
              <FormControlLabel
                value="none" control={<Radio size="small" />}
                label={<Typography variant="body2">None</Typography>}
                disabled={isDisabled}
              />
              <FormControlLabel
                value="bearer" control={<Radio size="small" />}
                label={<Typography variant="body2">Bearer Token</Typography>}
                disabled={isDisabled}
              />
              <FormControlLabel
                value="api_key" control={<Radio size="small" />}
                label={<Typography variant="body2">API Key</Typography>}
                disabled={isDisabled}
              />
              <FormControlLabel
                value="login_endpoint" control={<Radio size="small" />}
                label={<Typography variant="body2">Login Endpoint</Typography>}
                disabled={isDisabled}
              />
            </RadioGroup>

            {/* Bearer Token fields */}
            {authType === 'bearer' && (
              <Box sx={{ mt: 1, pl: 3.5 }}>
                <TextField
                  size="small" fullWidth
                  label="Bearer Token"
                  type="password"
                  value={bearerToken}
                  onChange={(e) => setBearerToken(e.target.value)}
                  disabled={isDisabled}
                  placeholder="eyJhbGciOiJIUzI1NiIs..."
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
                />
              </Box>
            )}

            {/* API Key fields */}
            {authType === 'api_key' && (
              <Box sx={{ mt: 1, pl: 3.5, display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Header Name"
                  value={apiKeyHeader}
                  onChange={(e) => setApiKeyHeader(e.target.value)}
                  disabled={isDisabled}
                  sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
                />
                <TextField
                  size="small" fullWidth
                  label="API Key Value"
                  type="password"
                  value={apiKeyValue}
                  onChange={(e) => setApiKeyValue(e.target.value)}
                  disabled={isDisabled}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
                />
              </Box>
            )}

            {/* Login Endpoint fields */}
            {authType === 'login_endpoint' && (
              <Box sx={{ mt: 1, pl: 3.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="Method"
                    value={loginMethod}
                    onChange={(e) => setLoginMethod(e.target.value)}
                    disabled={isDisabled}
                    sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                  />
                  <TextField
                    size="small" fullWidth
                    label="Login URL"
                    value={loginUrl}
                    onChange={(e) => setLoginUrl(e.target.value)}
                    disabled={isDisabled}
                    placeholder="https://example.com/api/auth/login"
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
                  />
                </Box>
                <TextField
                  size="small" fullWidth multiline minRows={2} maxRows={4}
                  label="Request Body (JSON)"
                  value={loginBody}
                  onChange={(e) => setLoginBody(e.target.value)}
                  disabled={isDisabled}
                  placeholder='{"email": "test@example.com", "password": "..."}'
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', fontFamily: 'monospace' } }}
                />
                <TextField
                  size="small" fullWidth
                  label="Token Path in Response"
                  value={tokenPath}
                  onChange={(e) => setTokenPath(e.target.value)}
                  disabled={isDisabled}
                  placeholder="data.token"
                  helperText="JSONPath to extract token (e.g. data.access_token)"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      {confirmError && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {confirmError}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Tooltip
          title={!isAuthorized ? 'Authorize the domain above before starting the test.' : ''}
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="success"
              startIcon={<StartIcon />}
              onClick={handleConfirm}
              disabled={isDisabled || !isAuthorized || (endpoints.length > 0 && noneSelected)}
              size="medium"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitted ? 'Starting...' : `Start Testing${endpoints.length > 0 ? ` (${selectedIndices.size} endpoints)` : ''}`}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={isDisabled}
          size="medium"
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SlavePreflightCard;
